"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuthoringScope } from "../authoring-scope";
import { DrillIcon } from "./icon";
import { DrillError, useLeaveWarning } from "./shared";
import {
  type DrillCollectionDetail,
  type DrillCollectionOrder,
  type DrillCollectionOrderSaved,
  type DrillCollectionOrderWrite,
  RequestError,
  request,
} from "./types";
import "./collection-order.css";

export function DrillCollectionOrderEditor({
  collection,
  onSaved,
  onCancel,
}: {
  collection: DrillCollectionDetail;
  onSaved: (collection: DrillCollectionDetail) => void;
  onCancel: () => void;
}) {
  const authoring = useAuthoringScope();
  const path = authoring.api(`/api/drill-collections/${collection.id}`);
  const [original, setOriginal] = useState<DrillCollectionOrder>();
  const [drills, setDrills] = useState<DrillCollectionOrder["drills"]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const heading = useRef<HTMLHeadingElement>(null);
  const errorRegion = useRef<HTMLDivElement>(null);
  const dirty =
    !!original &&
    drills.some((drill, index) => drill.id !== original.drills[index]?.id);
  useLeaveWarning(dirty && !saved);
  useEffect(() => {
    if (error) {
      errorRegion.current?.focus();
      errorRegion.current?.scrollIntoView({ block: "center" });
    }
  }, [error]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const order = await request<DrillCollectionOrder>(`${path}/drill-order`);
      setOriginal(order);
      setDrills(order.drills);
      setConflict(false);
      setAnnouncement("最新の順序を読み込みました。");
    } catch (error) {
      setError((error as Error).message);
    } finally {
      setLoading(false);
    }
  }, [path]);
  useEffect(() => {
    heading.current?.focus();
    void load();
  }, [load]);

  function move(index: number, direction: -1 | 1, button: HTMLButtonElement) {
    const next = index + direction;
    if (saving || saved || next < 0 || next >= drills.length) return;
    const reordered = [...drills];
    const drill = reordered[index];
    const adjacent = reordered[next];
    if (!drill || !adjacent) return;
    reordered[index] = adjacent;
    reordered[next] = drill;
    setDrills(reordered);
    setAnnouncement(`${drill.title}を${next + 1}番目に移動しました。`);
    requestAnimationFrame(() => {
      button.focus({ preventScroll: true });
      button.closest("li")?.scrollIntoView({ block: "nearest" });
    });
  }

  async function save() {
    if (!original || saving || (!dirty && !saved)) return;
    setSaving(true);
    setError("");
    try {
      if (!saved) {
        const body: DrillCollectionOrderWrite = {
          revision: original.revision,
          drillIds: drills.map((drill) => drill.id),
        };
        await request<DrillCollectionOrderSaved>(`${path}/drill-order`, body);
        setSaved(true);
      }
      // If this reload fails after saving, retry only the reload. The saved
      // revision must never be posted a second time from this editor.
      onSaved(await request<DrillCollectionDetail>(path));
    } catch (error) {
      setConflict(error instanceof RequestError && error.status === 409);
      setError((error as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      className="drill-order"
      aria-labelledby="drill-order-heading"
      aria-busy={loading || saving}
    >
      <div className="drill-order-heading">
        <div>
          <h1 id="drill-order-heading" tabIndex={-1} ref={heading}>
            ドリルを並べ替え
          </h1>
          <p>{collection.title}</p>
        </div>
        <span className="drill-order-count">
          {loading ? "読み込み中…" : `${drills.length}件`}
        </span>
      </div>
      <p className="drill-order-hint">
        上下のボタンで順序を変更し、保存してください。
      </p>
      {!authoring.admin && (
        <p className="drill-order-hint">
          変更した順序は、各グループ内の表示に反映されます。グループの順番は練習状況で決まります。
        </p>
      )}
      {error && (
        <div ref={errorRegion} tabIndex={-1}>
          <DrillError
            message={
              saved
                ? `順序は保存しました。一覧の再読み込みに失敗しました。${error}`
                : error
            }
          />
        </div>
      )}
      {conflict && (
        <button
          type="button"
          className="secondary-button"
          onClick={() => void load()}
          disabled={loading || saving}
        >
          変更を破棄して最新の順序を読み込む
        </button>
      )}
      {!original && error && (
        <button
          type="button"
          className="secondary-button"
          onClick={() => void load()}
          disabled={loading}
        >
          再読み込み
        </button>
      )}
      <p className="drill-sr-only" role="status" aria-live="polite">
        {announcement}
      </p>
      {!loading && original && (
        <ol className="drill-order-list" aria-label="ドリルの表示順">
          {drills.map((drill, index) => (
            <li key={drill.id}>
              <span className="drill-order-number" aria-hidden="true">
                {index + 1}
              </span>
              <span className="drill-order-title">{drill.title}</span>
              <div className="drill-order-controls">
                <button
                  type="button"
                  aria-label={`${drill.title}を上へ移動`}
                  aria-disabled={index === 0}
                  disabled={saving || saved || conflict}
                  onClick={(event) => move(index, -1, event.currentTarget)}
                >
                  <DrillIcon name="up" size={19} />
                </button>
                <button
                  type="button"
                  aria-label={`${drill.title}を下へ移動`}
                  aria-disabled={index === drills.length - 1}
                  disabled={saving || saved || conflict}
                  onClick={(event) => move(index, 1, event.currentTarget)}
                >
                  <DrillIcon name="down" size={19} />
                </button>
              </div>
            </li>
          ))}
        </ol>
      )}
      <div className="drill-order-actions">
        {!saved && (
          <button
            type="button"
            className="secondary-button"
            disabled={saving}
            onClick={onCancel}
          >
            キャンセル
          </button>
        )}
        <button
          type="button"
          className="primary-button"
          disabled={loading || saving || conflict || (!dirty && !saved)}
          onClick={() => void save()}
        >
          {saving
            ? saved
              ? "再読み込み中…"
              : "保存中…"
            : saved
              ? "一覧を再読み込み"
              : "順序を保存"}
        </button>
      </div>
    </section>
  );
}
